import { resetCf7ModalSuccess } from "./cf7-modal-response.js";

function parseHashes(modal) {
  const raw = modal.getAttribute("data-modal-hashes") || "#service-offer";
  return raw
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .map((part) => (part.startsWith("#") ? part : `#${part}`));
}

function normalizeHash(hash) {
  if (!hash) {
    return "";
  }
  const value = hash.trim().toLowerCase();
  return value.startsWith("#") ? value : `#${value}`;
}

function setFormField(form, name, value) {
  if (!form || !name) {
    return;
  }

  const field = form.querySelector(`input[name="${name}"], textarea[name="${name}"]`);
  if (field) {
    field.value = value ?? "";
  }
}

function getPricingPlanFromTrigger(trigger) {
  if (!trigger) {
    return "";
  }

  if (trigger.hasAttribute("data-service-pricing-plan")) {
    return trigger.getAttribute("data-service-pricing-plan") || "";
  }

  const scoped = trigger.closest("[data-service-pricing-plan]");
  if (scoped) {
    return scoped.getAttribute("data-service-pricing-plan") || "";
  }

  return "";
}

export default function initServiceModal(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  const modal = scope.querySelector("[data-service-modal]");

  if (!modal || modal.__serviceModalBound) {
    return;
  }

  modal.__serviceModalBound = true;

  const hashTriggers = new Set(parseHashes(modal));
  const closeButtons = modal.querySelectorAll("[data-service-modal-close]");
  const defaultServiceName = modal.getAttribute("data-service-name") || "";
  const defaultServiceUrl = modal.getAttribute("data-service-url") || "";
  const defaultServiceId = modal.getAttribute("data-service-id") || "";
  let lastActiveElement = null;
  let activePricingPlan = "";

  const getForm = () => modal.querySelector(".wpcf7-form");

  const updateContextDisplay = (pricingPlan = "") => {
    const serviceDisplay = modal.querySelector('[data-service-modal-display="service-name"]');
    const planDisplay = modal.querySelector('[data-service-modal-display="pricing-plan"]');
    const planRow = modal.querySelector("[data-service-modal-plan-row]");

    if (serviceDisplay) {
      serviceDisplay.textContent = defaultServiceName;
    }

    if (planRow && planDisplay) {
      const hasPlan = Boolean(pricingPlan);
      planRow.hidden = !hasPlan;
      planDisplay.textContent = hasPlan ? pricingPlan : "";
    }
  };

  const applyContext = (pricingPlan = "") => {
    updateContextDisplay(pricingPlan);

    const form = getForm();
    if (!form) {
      return;
    }

    setFormField(form, "service-name", defaultServiceName);
    setFormField(form, "service-url", defaultServiceUrl);
    setFormField(form, "service-id", defaultServiceId);
    setFormField(form, "pricing-plan", pricingPlan);
  };

  const isTriggerHref = (href) => {
    if (!href) {
      return false;
    }

    const normalized = normalizeHash(href);
    if (hashTriggers.has(normalized)) {
      return true;
    }

    if (!href.includes("#")) {
      return false;
    }

    try {
      const parsed = new URL(href, window.location.href);
      return hashTriggers.has(normalizeHash(`#${parsed.hash.replace(/^#/, "")}`));
    } catch {
      return false;
    }
  };

  const openModal = (trigger) => {
    resetCf7ModalSuccess(modal);
    activePricingPlan = getPricingPlanFromTrigger(trigger);
    applyContext(activePricingPlan);
    lastActiveElement = document.activeElement;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    modal.classList.add("is-open");
    document.documentElement.classList.add("global-modal-open");

    const focusable = modal.querySelector(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    if (focusable && typeof focusable.focus === "function") {
      focusable.focus();
    }
  };

  const closeModal = () => {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal.hidden = true;
    document.documentElement.classList.remove("global-modal-open");
    activePricingPlan = "";
    applyContext("");

    if (window.location.hash && hashTriggers.has(normalizeHash(window.location.hash))) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    if (lastActiveElement && typeof lastActiveElement.focus === "function") {
      lastActiveElement.focus();
    }
  };

  const openFromHash = () => {
    if (isTriggerHref(window.location.hash)) {
      openModal(null);
    }
  };

  scope.addEventListener("click", (event) => {
    const trigger = event.target.closest("a[href], [data-service-modal-open]");
    if (!trigger || !document.body.contains(trigger)) {
      return;
    }

    const hasOpenAttr = trigger.hasAttribute("data-service-modal-open");
    const href = trigger.getAttribute("href") || "";
    const isHashTrigger = isTriggerHref(href);

    if (!hasOpenAttr && !isHashTrigger) {
      return;
    }

    event.preventDefault();
    openModal(trigger);
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      closeModal();
    });
  });

  document.addEventListener("keydown", (event) => {
    if (!modal.classList.contains("is-open")) {
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
    }
  });

  window.addEventListener("hashchange", openFromHash);

  document.addEventListener("wpcf7init", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLElement) || !modal.contains(form)) {
      return;
    }
    applyContext(activePricingPlan);
  });

  if (isTriggerHref(window.location.hash)) {
    openFromHash();
  }
}
